import React from 'react';
import { useCurrency } from '../App';

/**
 * PriceDisplay - Consistent price formatting component
 * 
 * @param {number} amount - The price amount
 * @param {string} fromCurrency - Source currency ('USD' or 'XCG'), default 'USD'
 * @param {string} size - Size variant: 'sm', 'md', 'lg', 'xl'
 * @param {string} className - Additional classes
 * @param {boolean} convert - Whether to convert to user's preferred currency (default true)
 * @param {string} unit - Optional price unit suffix (e.g., 'm²', 'hectare', 'stuk')
 */
const PriceDisplay = ({ 
  amount, 
  fromCurrency = 'USD', 
  size = 'md',
  className = '',
  convert = true,
  unit = null
}) => {
  const { formatDualPrice, currency } = useCurrency();
  
  if (amount === null || amount === undefined) return null;
  
  let displayPrice;
  
  if (convert) {
    // Convert to user's preferred currency
    const prices = formatDualPrice(amount, fromCurrency);
    displayPrice = currency === 'XCG' ? prices.xcg : prices.usd;
  } else {
    // No conversion - just format the number with the specified currency symbol
    const symbol = fromCurrency === 'USD' ? '$' : 'XCG';
    const formatted = amount.toLocaleString('nl-NL', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    displayPrice = fromCurrency === 'USD' ? `$ ${formatted}` : `XCG ${formatted}`;
  }
  
  const sizeClasses = {
    sm: 'text-sm font-semibold',
    md: 'text-lg font-bold',
    lg: 'text-2xl font-bold',
    xl: 'text-3xl sm:text-4xl font-bold'
  };
  
  return (
    <span className={`${sizeClasses[size] || sizeClasses.md} text-foreground ${className}`}>
      {displayPrice}
      {unit && <span className="text-sm font-normal text-muted-foreground ml-1">/{unit}</span>}
    </span>
  );
};

export default PriceDisplay;
